import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY_PREFIX = 'archipro:visited-website:';

/**
 * Persist that the visitor has followed a "Visit Website" link for this page.
 * Standalone so click handlers outside a `useWebsiteVisited` host — the tile
 * actions, which never render the tick — can record the visit without mounting
 * a per-tile effect just to read the flag back.
 */
export const markWebsiteVisited = (pageID: number | undefined): void => {
    if (pageID === undefined || typeof window === 'undefined') {
        return;
    }
    try {
        localStorage.setItem(STORAGE_KEY_PREFIX + String(pageID), '1');
    } catch {
        // localStorage may be full or unavailable — silently ignore.
    }
};

/**
 * Tracks whether the user has previously clicked a "Visit Website" link
 * for a specific page. Persists to localStorage so the tick indicator
 * survives page navigation and browser sessions.
 *
 * Keyed by `pageID` (the library item / siteTreeID) so that different
 * products from the same supplier are tracked independently, even if they
 * share the same external website URL.
 *
 * @param pageID The unique ID of the page (product, professional, project,
 *   article). Pass `undefined` if not yet known (the hook will be inert).
 * @returns `hasVisited` — whether the link has been clicked before.
 * @returns `markVisited` — call on click to persist the visited state.
 */
export function useWebsiteVisited(pageID: number | undefined) {
    // Start `false` so the server and first client render agree — reading
    // localStorage here would diff against SSR and break hydration (React #418).
    const [hasVisited, setHasVisited] = useState<boolean>(false);

    useEffect(() => {
        if (pageID === undefined) {
            setHasVisited(false);
            return;
        }
        const key = STORAGE_KEY_PREFIX + String(pageID);
        try {
            // Set the exact value so it resets when navigating to a page that
            // has not been visited (the hook instance is reused across nav).
            setHasVisited(localStorage.getItem(key) === '1');
        } catch {
            setHasVisited(false);
        }
    }, [pageID]);

    const markVisited = useCallback(() => {
        markWebsiteVisited(pageID);
        if (pageID !== undefined && typeof window !== 'undefined') {
            setHasVisited(true);
        }
    }, [pageID]);

    return { hasVisited, markVisited } as const;
}
